import type { ThreadMessage } from "@assistant-ui/react";
import { conversationSummaryCopyText } from "./ConversationSummary";
import type { ConversationSummaryData } from "./ConversationSummary.types";
import { GENUI_TOOL_NAME } from "./part";

/** A display component's content as plain text, or null when it has none worth copying. */
export type GenUICopyText = (props: Record<string, unknown>) => string | null;

/**
 * What each GenUI component contributes to "Copy message". A card whose content IS the
 * answer (e.g. a conversation summary) must be copyable even when the turn has no text part;
 * a component missing from this map contributes nothing, as before.
 */
export const genUICopyText: Record<string, GenUICopyText> = {
  render_conversation_summary: (props) =>
    conversationSummaryCopyText(props as unknown as ConversationSummaryData),
};

type MessagePart = ThreadMessage["content"][number];

function genuiPartText(part: MessagePart): string | null {
  if (part.type !== "tool-call" || part.toolName !== GENUI_TOOL_NAME) {
    return null;
  }
  const { component, props } = (part.args ?? {}) as {
    component?: unknown;
    props?: Record<string, unknown>;
  };
  const toText = typeof component === "string" ? genUICopyText[component] : undefined;
  return toText ? toText(props ?? {}) : null;
}

/**
 * A message's copy text: its text parts plus the copy text of any GenUI components, in
 * order, joined by blank lines. For a text-only message this matches assistant-ui's own
 * `getCopyText`. Live and replayed messages both carry the same genui tool-call part, so
 * both copy the same text.
 */
export function messageCopyText(parts: readonly MessagePart[]): string {
  return parts
    .map((part) => (part.type === "text" ? part.text : genuiPartText(part)))
    .filter((text): text is string => typeof text === "string" && text.length > 0)
    .join("\n\n");
}
